import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { Check, CaretDown, CaretUp, FloppyDisk, Lightning } from '@phosphor-icons/react';

/**
 * Matrix-editor voor commissies: rijen = categorieën, kolommen = verkopers.
 * Elke cel heeft % en €/st (stapelbaar) + basis.
 * "Bulk-vul" knop per kolom: zet één waarde in alle categorieën tegelijk.
 */
export default function CommissieMatrix({ startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  const [verkopers, setVerkopers] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [cellen, setCellen] = useState({}); // key: `${vid}|${cat}` → {pct, vast, basis}
  const [laadStatus, setLaadStatus] = useState('idle'); // idle|loading|ready|saving|ok|fout
  const [msg, setMsg] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(null); // vid van verkoper waarvan bulk open is
  const [bulkWaarde, setBulkWaarde] = useState({ pct: 0, vast: 0, basis: 'winst', pctText: '', vastText: '' });
  const [bulkGeselecteerd, setBulkGeselecteerd] = useState({}); // vid → {cat: true}

  const toggleBulkCat = (vid, cat) => {
    setBulkGeselecteerd(p => {
      const n = { ...(p[vid] || {}) };
      if (n[cat]) delete n[cat]; else n[cat] = true;
      return { ...p, [vid]: n };
    });
  };
  const bulkAllesKiezen = (vid, aan) => {
    setBulkGeselecteerd(p => ({ ...p, [vid]: aan ? Object.fromEntries(categorieen.map(c => [c, true])) : {} }));
  };

  const load = async () => {
    setLaadStatus('loading');
    try {
      const [vs, params] = await Promise.all([
        API.get('/verkopers'),
        API.get('/artikel-params'),
      ]);
      const actieveVerkopers = (vs.data || []).filter(v => v.actief !== false);
      setVerkopers(actieveVerkopers);
      const cats = (params.data?.categorieen || []).filter(Boolean);
      setCategorieen(cats);
      // Bouw de matrix vanuit bestaande commissie_regels (scope=categorie).
      // pctText/vastText bewaren de RAW tekst (met komma) zodat tijdens typen de
      // decimaal-punt/komma niet wordt gestript door React's re-render → anders
      // typt de gebruiker "5,5" en ziet hij "55" (data-verlies).
      const init = {};
      for (const v of actieveVerkopers) {
        for (const cat of cats) {
          const regel = (v.commissie_regels || []).find(r => r.scope === 'categorie' && (r.doel || '').toLowerCase() === cat.toLowerCase());
          const pct = regel ? Number(regel.pct || 0) : 0;
          const vast = regel ? Number(regel.vast_per_stuk || 0) : 0;
          init[`${v.id}|${cat}`] = {
            pct, vast,
            basis: regel?.basis || 'winst',
            pctText: pct === 0 ? '' : String(pct).replace('.', ','),
            vastText: vast === 0 ? '' : String(vast).replace('.', ','),
          };
        }
      }
      setCellen(init);
      setLaadStatus('ready');
    } catch (e) {
      setLaadStatus('fout');
      setMsg({ text: 'Laden mislukt: ' + (e.response?.data?.detail || e.message), kind: 'err' });
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const setCel = (vid, cat, patch) => {
    setCellen(p => ({ ...p, [`${vid}|${cat}`]: { ...p[`${vid}|${cat}`], ...patch } }));
  };

  // Bulk-vul voor één verkoper: pas pct/vast/basis toe op geselecteerde categorieën (of alles als niks geselecteerd)
  const bulkVul = (vid) => {
    const sel = bulkGeselecteerd[vid] || {};
    const geselecteerd = Object.keys(sel).filter(k => sel[k]);
    const doelCats = geselecteerd.length > 0 ? geselecteerd : categorieen;
    setCellen(p => {
      const nw = { ...p };
      for (const cat of doelCats) {
        nw[`${vid}|${cat}`] = {
          pct: bulkWaarde.pct || 0,
          vast: bulkWaarde.vast || 0,
          basis: bulkWaarde.basis || 'winst',
          // Sync text-velden zodat cell-inputs het direct tonen
          pctText: (bulkWaarde.pct || 0) === 0 ? '' : String(bulkWaarde.pct).replace('.', ','),
          vastText: (bulkWaarde.vast || 0) === 0 ? '' : String(bulkWaarde.vast).replace('.', ','),
        };
      }
      return nw;
    });
    setBulkOpen(null);
    setBulkGeselecteerd(p => ({ ...p, [vid]: {} }));
    setMsg({ text: `Bulk-korting toegepast op ${doelCats.length} categorie(ën) — klik "Opslaan" om permanent te bewaren`, kind: 'info' });
  };

  // Opslaan: verzamel voor elke verkoper de commissie_regels die niet-nul zijn
  const opslaan = async () => {
    setLaadStatus('saving');
    setMsg(null);
    try {
      for (const v of verkopers) {
        // Behoud bestaande NIET-categorie regels (artikel + prijsgroep)
        const overigeRegels = (v.commissie_regels || []).filter(r => r.scope !== 'categorie');
        // Voeg nieuwe categorie-regels toe
        const categorieRegels = [];
        for (const cat of categorieen) {
          const c = cellen[`${v.id}|${cat}`] || { pct: 0, vast: 0, basis: 'winst' };
          if ((Number(c.pct) || 0) > 0 || (Number(c.vast) || 0) > 0) {
            categorieRegels.push({
              scope: 'categorie',
              doel: cat,
              pct: Number(c.pct) || 0,
              vast_per_stuk: Number(c.vast) || 0,
              basis: c.basis || 'winst',
            });
          }
        }
        const allesRegels = [...overigeRegels, ...categorieRegels];
        await API.put(`/verkopers/${v.id}`, { commissie_regels: allesRegels });
      }
      setLaadStatus('ok');
      setMsg({ text: `✓ Commissies opgeslagen voor ${verkopers.length} verkoper(s)`, kind: 'ok' });
      // Toon succes-feedback minstens 2,5s voor we de DB opnieuw inlezen (anders zet load() de status meteen op 'ready').
      setTimeout(async () => {
        setMsg(null);
        await load(); // Stille herlading om UI synchroon te houden met DB
      }, 2500);
    } catch (e) {
      setLaadStatus('fout');
      setMsg({ text: 'Opslaan mislukt: ' + (e.response?.data?.detail || e.message), kind: 'err' });
    }
  };

  const totaalGevuld = useMemo(() => {
    let n = 0;
    for (const k of Object.keys(cellen)) {
      const c = cellen[k] || {};
      if ((Number(c.pct) || 0) > 0 || (Number(c.vast) || 0) > 0) n++;
    }
    return n;
  }, [cellen]);

  return (
    <div className="mb-5" data-testid="commissie-matrix-section">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-3 bg-[#FFF7ED] border-2 border-[#F97316]/40 hover:bg-[#FFEDD5] transition-colors"
        data-testid="commissie-matrix-toggle"
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">🎯</span>
          <span className="text-left">
            <span className="text-sm font-bold text-gray-900 block">Commissie-matrix — per verkoper × categorie</span>
            <span className="text-[11px] text-gray-600">Beheer alle categorie-commissies in één grid. Bulk-vul per verkoper beschikbaar.</span>
          </span>
        </span>
        {open ? <CaretUp size={16} className="text-[#F97316]" weight="bold" /> : <CaretDown size={16} className="text-[#F97316]" weight="bold" />}
      </button>

      {open && (
        <div className="border-2 border-[#F97316]/40 border-t-0 bg-white p-3">
          {msg && (
            <div className={`mb-2 px-3 py-2 text-xs font-medium ${msg.kind === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : msg.kind === 'err' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
              {msg.text}
            </div>
          )}

          {laadStatus === 'loading' && <div className="text-center py-8 text-sm text-gray-500">Laden...</div>}
          {laadStatus !== 'loading' && categorieen.length === 0 && <div className="text-center py-8 text-sm text-gray-500">Geen categorieën. Voeg eerst artikel-categorieën toe in Instellingen → Artikels.</div>}
          {laadStatus !== 'loading' && verkopers.length === 0 && <div className="text-center py-8 text-sm text-gray-500">Geen actieve verkopers.</div>}

          {categorieen.length > 0 && verkopers.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2 text-[11px] text-gray-600">
                <span>
                  <strong className="text-gray-900">{categorieen.length}</strong> categorieën × <strong className="text-gray-900">{verkopers.length}</strong> verkopers ·
                  <span className="ml-1">{totaalGevuld} cellen ingevuld</span>
                </span>
                <span className="italic">Pct = % op basis · €/st = vast bedrag per stuk · Beide stapelbaar</span>
              </div>

              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left border-b border-gray-200" style={{ minWidth: 140, position: 'sticky', left: 0, background: '#F9FAFB', zIndex: 11 }}>Categorie</th>
                      {verkopers.map(v => (
                        <th key={v.id} className="px-2 py-2 text-left border-b border-l border-gray-200" style={{ minWidth: 260 }}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-bold text-gray-900 text-sm">{v.naam}</span>
                            <button
                              type="button"
                              onClick={() => { setBulkOpen(bulkOpen === v.id ? null : v.id); setBulkWaarde({ pct: 0, vast: 0, basis: 'winst', pctText: '', vastText: '' }); }}
                              className="text-xs font-bold text-white bg-[#F97316] hover:bg-[#EA580C] px-2 py-1 inline-flex items-center gap-1 rounded"
                              title="Bulk-vul korting/commissie voor alle categorieën van deze verkoper"
                              data-testid={`bulk-toggle-${v.id}`}
                            >
                              <Lightning size={12} weight="fill" /> Bulk-korting
                            </button>
                          </div>
                          {bulkOpen === v.id && (() => {
                            const sel = bulkGeselecteerd[v.id] || {};
                            const gekozen = Object.keys(sel).filter(k => sel[k]);
                            const allesGekozen = gekozen.length === categorieen.length;
                            const doelLabel = gekozen.length === 0 ? `alle ${categorieen.length}` : `${gekozen.length} gekozen`;
                            return (
                            <div className="mt-2 p-2 bg-[#FFF7ED] border-2 border-[#F97316] space-y-1.5" onClick={e => e.stopPropagation()}>
                              <div className="text-[11px] font-bold text-[#EA580C] mb-1">Bulk-korting instellen:</div>

                              {/* Stap 1 — Kies categorieën */}
                              <div className="bg-white border border-orange-200 p-1.5">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold text-gray-700">1. Kies categorieën</span>
                                  <button type="button" onClick={() => bulkAllesKiezen(v.id, !allesGekozen)} className="text-[10px] text-[#F97316] hover:underline font-bold">
                                    {allesGekozen ? 'Alles uit' : 'Alles aan'}
                                  </button>
                                </div>
                                <div className="max-h-32 overflow-y-auto space-y-0.5 text-[11px]">
                                  {categorieen.map(cat => (
                                    <label key={cat} className="flex items-center gap-1.5 hover:bg-orange-50 cursor-pointer px-1 py-0.5">
                                      <input
                                        type="checkbox"
                                        checked={!!sel[cat]}
                                        onChange={() => toggleBulkCat(v.id, cat)}
                                        className="accent-[#F97316]"
                                        data-testid={`bulk-cat-${v.id}-${cat}`}
                                      />
                                      <span>{cat}</span>
                                    </label>
                                  ))}
                                </div>
                                <div className="text-[9px] text-gray-500 mt-1 italic">Niets geselecteerd = alle {categorieen.length} categorieën</div>
                              </div>

                              {/* Stap 2 — Waardes */}
                              <div className="bg-white border border-orange-200 p-1.5">
                                <div className="text-[10px] font-bold text-gray-700 mb-1">2. Korting/commissie</div>
                                <div className="grid grid-cols-2 gap-1.5">
                                  <div>
                                    <label className="text-[10px] text-gray-600 block">Percentage</label>
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="text" inputMode="decimal"
                                        className="field-input text-sm font-mono flex-1"
                                        style={{ padding: '4px 6px' }}
                                        placeholder="0"
                                        value={bulkWaarde.pctText ?? ''}
                                        onChange={e => {
                                          const raw = e.target.value || '';
                                          const parsed = parseFloat(raw.replace(',', '.'));
                                          setBulkWaarde(p => ({ ...p, pctText: raw, pct: isNaN(parsed) ? 0 : parsed }));
                                        }}
                                      />
                                      <span className="text-[11px] text-gray-500 font-bold">%</span>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-gray-600 block">Vast</label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-[11px] text-gray-500 font-bold">€</span>
                                      <input
                                        type="text" inputMode="decimal"
                                        className="field-input text-sm font-mono flex-1"
                                        style={{ padding: '4px 6px' }}
                                        placeholder="0"
                                        value={bulkWaarde.vastText ?? ''}
                                        onChange={e => {
                                          const raw = e.target.value || '';
                                          const parsed = parseFloat(raw.replace(',', '.'));
                                          setBulkWaarde(p => ({ ...p, vastText: raw, vast: isNaN(parsed) ? 0 : parsed }));
                                        }}
                                      />
                                      <span className="text-[10px] text-gray-500">/st</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-1">
                                  <label className="text-[10px] text-gray-600 block">% Berekenen op</label>
                                  <select className="field-input text-sm w-full" style={{ padding: '4px 6px' }} value={bulkWaarde.basis} onChange={e => setBulkWaarde(p => ({ ...p, basis: e.target.value }))}>
                                    <option value="winst">Winst (aanbevolen)</option>
                                    <option value="verkoop">Omzet (excl BTW)</option>
                                  </select>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => bulkVul(v.id)}
                                className="w-full text-xs font-bold bg-[#F97316] hover:bg-[#EA580C] text-white py-1.5 inline-flex items-center justify-center gap-1"
                                data-testid={`bulk-apply-${v.id}`}
                              >
                                <Check size={13} weight="bold" /> Toepassen op {doelLabel}
                              </button>
                            </div>
                            );
                          })()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categorieen.map((cat, idx) => (
                      <tr key={cat} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                        <td className="px-2 py-1.5 font-medium text-gray-900 border-b border-gray-100" style={{ position: 'sticky', left: 0, background: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB', zIndex: 5 }}>{cat}</td>
                        {verkopers.map(v => {
                          const c = cellen[`${v.id}|${cat}`] || { pct: 0, vast: 0, basis: 'winst' };
                          const isGevuld = (Number(c.pct) || 0) > 0 || (Number(c.vast) || 0) > 0;
                          return (
                            <td key={v.id} className={`px-2 py-1.5 border-b border-l border-gray-100 ${isGevuld ? 'bg-green-50/60' : ''}`} data-testid={`matrix-cel-${v.id}-${cat}`}>
                              <div className="flex items-center gap-1">
                                {/* type="text" + inputMode="decimal": accepteert beide komma EN punt.
                                    pctText/vastText bewaren de raw tekst zodat React's re-render geen
                                    decimaalteken stript tijdens het typen (bug: "2,2" → "22"). */}
                                <input
                                  type="text" inputMode="decimal"
                                  className="font-mono text-sm border border-gray-300 bg-white text-right px-1.5 py-1 focus:border-[#F97316] focus:outline-none"
                                  style={{ width: 58 }}
                                  value={c.pctText ?? ''}
                                  onChange={e => {
                                    const raw = e.target.value || '';
                                    const parsed = parseFloat(raw.replace(',', '.'));
                                    setCel(v.id, cat, {
                                      pctText: raw,
                                      pct: isNaN(parsed) ? 0 : parsed,
                                    });
                                  }}
                                  placeholder="0"
                                  title="Percentage (komma of punt toegestaan)"
                                />
                                <span className="text-[10px] text-gray-500 font-bold">%</span>
                                <input
                                  type="text" inputMode="decimal"
                                  className="font-mono text-sm border border-gray-300 bg-white text-right px-1.5 py-1 focus:border-[#F97316] focus:outline-none"
                                  style={{ width: 58 }}
                                  value={c.vastText ?? ''}
                                  onChange={e => {
                                    const raw = e.target.value || '';
                                    const parsed = parseFloat(raw.replace(',', '.'));
                                    setCel(v.id, cat, {
                                      vastText: raw,
                                      vast: isNaN(parsed) ? 0 : parsed,
                                    });
                                  }}
                                  placeholder="0"
                                  title="Vast bedrag per stuk (komma of punt toegestaan)"
                                />
                                <span className="text-[10px] text-gray-500 font-bold">€/st</span>
                                <select
                                  value={c.basis || 'winst'}
                                  onChange={e => setCel(v.id, cat, { basis: e.target.value })}
                                  className="text-[11px] font-bold border border-gray-300 bg-white px-1 py-1 focus:border-[#F97316] focus:outline-none"
                                  style={{ width: 54 }}
                                  title="Basis voor percentage"
                                >
                                  <option value="winst">winst</option>
                                  <option value="verkoop">omzet</option>
                                </select>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 flex-wrap gap-2">
                <span className="text-[11px] text-gray-500 italic">
                  Groene cellen = actieve regel. <strong>winst</strong> = % op winstmarge · <strong>omzet</strong> = % op bedrag excl BTW.
                </span>
                <div className="flex items-center gap-2">
                  {/* Inline succes-feedback dichtbij de Opslaan-knop — anders ziet de gebruiker
                      de banner bovenaan niet (hij focust op de cellen + knop) */}
                  {laadStatus === 'ok' && (
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 border-2 border-green-500 text-green-800 text-xs font-bold rounded animate-pulse"
                      data-testid="commissie-matrix-succes-inline"
                    >
                      <Check size={14} weight="bold" /> Opgeslagen!
                    </span>
                  )}
                  {laadStatus === 'fout' && (
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 border-2 border-red-500 text-red-800 text-xs font-bold"
                      data-testid="commissie-matrix-fout-inline"
                    >
                      ✗ Mislukt — check melding bovenaan
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={opslaan}
                    disabled={laadStatus === 'saving'}
                    className={`btn font-bold inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors ${
                      laadStatus === 'ok' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-[#F97316] hover:bg-[#EA580C] text-white'
                    }`}
                    data-testid="commissie-matrix-opslaan"
                  >
                    {laadStatus === 'saving' ? (
                      <><FloppyDisk size={14} weight="bold" className="animate-pulse" /> Opslaan…</>
                    ) : laadStatus === 'ok' ? (
                      <><Check size={14} weight="bold" /> Opgeslagen ({totaalGevuld} cellen)</>
                    ) : (
                      <><FloppyDisk size={14} weight="bold" /> Opslaan ({totaalGevuld} cellen)</>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
